import { useModal } from '@ebay/nice-modal-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import useAppleLogin from '../../hooks/useAppleLogin'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { ThirdPartyPlatforms } from '../../types/common'
import type { UserInfo } from '../../types/user'
import type { AdjustAttributionMeta } from '../../utils/adjust'
import { getAdjustAttribution, getAdjustWebUUID } from '../../utils/adjust'
import { getAdsInfo } from '../../utils/ads-info'
import { api } from '../../utils/api-client'
import { getDeviceInfo, isIOS } from '../../utils/device'
import { getDeviceId } from '../../utils/device-id'
import { syncFcmToken } from '../../utils/fcmToken'
import { getFirstLandingUrl } from '../../utils/first-landing-url'
import { getPublicAuth } from '../../utils/getPublicAuth'
import { nativeSupportsOAuth } from '../../utils/nativeBridge'
import { encryptUser } from '../../utils/user'
import { watchPopupClosed } from '../../utils/watchPopupClosed'
import Icon from '../icon'
import { ToastMessage } from '.'
import Spinner from './spinner'

const OAUTH_POLL_INTERVAL_MS = 1500
const OAUTH_POLL_MAX_ITERATIONS = 120 // ~3 minutes at 1.5s cadence
const POLLS_AFTER_POPUP_CLOSED = 3
// Safety net for the whole native social Sign-In chain (bridge + native_login.php + link.php): if
// any step never resolves, this bounds it so the social buttons can't stay stuck-disabled.
const NATIVE_SIGN_IN_TIMEOUT_MS = 180_000

// Providers the lite app can sign in natively, handing back a JWT for native_login.php. The bridge
// exposes a method only where that provider is configured for the brand, so a missing method is the
// signal to fall back to browser OAuth — see `nativeSignIn`.
type NativeProvider = 'google' | 'facebook' | 'apple'

const NATIVE_PROVIDER_LABELS: Record<NativeProvider, string> = {
  google: 'Google',
  facebook: 'Facebook',
  apple: 'Apple',
}

// The bridge method for a provider, or undefined when this build can't sign in natively with it.
const nativeSignIn = (provider: NativeProvider) => {
  const bridge = window.Fun88Native
  if (provider === 'google') return bridge?.googleSignIn
  if (provider === 'facebook') return bridge?.facebookSignIn
  return bridge?.appleSignIn
}

// Icon-only social button (mirrors the Flutter app): borderless, no fill, 36px icon in 8px padding,
// centered. Icons keep their brand colours; a currentColor icon (Apple) is tinted by the button.
const ICON_BTN_CLASS =
  'flex items-center justify-center p-2 transition-opacity hover:opacity-80 disabled:opacity-50 [&>svg]:h-9 [&>svg]:w-9'

// const InstagramIcon = (
//   <svg width="40" height="40" viewBox="0 0 200 200">
//     <defs>
//       <radialGradient
//         id="radialGradient"
//         cx="-21.85"
//         cy="270.1"
//         r="416.95"
//         gradientUnits="userSpaceOnUse">
//         <stop offset="0" stopColor="#f9ed41" />
//         <stop offset="0.24" stopColor="#ff833d" />
//         <stop offset="0.4" stopColor="#ee5568" />
//         <stop offset="0.49" stopColor="#e7407b" />
//         <stop offset="1" stopColor="#0028ff" />
//       </radialGradient>
//     </defs>
//     <rect fill="url(#radialGradient)" width="200" height="200" rx="100" />
//     <path
//       fill="#fff"
//       d="M121.87,156.4H78.13A34.57,34.57,0,0,1,43.6,121.87V78.13A34.57,34.57,0,0,1,78.13,43.6h43.74A34.57,34.57,0,0,1,156.4,78.13v43.74A34.57,34.57,0,0,1,121.87,156.4ZM78.13,52A26.13,26.13,0,0,0,52,78.13v43.74A26.13,26.13,0,0,0,78.13,148h43.74A26.13,26.13,0,0,0,148,121.87V78.13A26.13,26.13,0,0,0,121.87,52Z"
//     />
//     <path
//       fill="#fff"
//       d="M100,128.53A28.53,28.53,0,1,1,128.53,100,28.56,28.56,0,0,1,100,128.53Zm0-48.42A19.89,19.89,0,1,0,119.88,100,19.91,19.91,0,0,0,100,80.11Z"
//     />
//     <path
//       fill="#fff"
//       d="M139.07,68.36a7.43,7.43,0,1,1-7.43-7.43A7.43,7.43,0,0,1,139.07,68.36Z"
//     />
//   </svg>
// )

interface SocialMediaAuthProps {
  callback?: (user: UserInfo, status: number) => void
  page: 'Login' | 'Register'
}

type AuthUrls = {
  [key in ThirdPartyPlatforms]?: {
    url: string
    id: string
  }
}

// Shared success payload for both OAuth flows: the polled browser callback (callback.php) and the
// native Google Sign-In endpoint (native_login.php) return the same shape.
type OAuthLoginInfo = UserInfo & {
  password?: string
  auth_id?: string
  is_new_register?: number
}

const SocialMediaAuth = ({ callback, page }: SocialMediaAuthProps) => {
  const params = useMemo(() => new URLSearchParams(window.location.search), [])
  const enableFacebook = useBoundStore(state => state.settings.login_facebook)
  const enableTelegram = useBoundStore(state => state.settings.login_telegram)
  const enableGoogle = useBoundStore(state => state.settings.login_google)
  const enableTwitter = useBoundStore(state => state.settings.login_twitter)
  const enableTwitch = useBoundStore(state => state.settings.login_twitch)
  const enableIos = useBoundStore(state => state.settings.login_ios)
  const enableTiktok = useBoundStore(state => state.settings.login_tiktok)
  // Show Apple where it can actually complete: the iOS lite app exposes the native bridge (works
  // regardless of the web rollout), so always show it there; on web/Android it follows the backend
  // `login_ios` rollout flag (the browser Apple OAuth path needs the Services ID live).
  const showApple = (!!window.Fun88Native?.appleSignIn || !!enableIos.value) && isIOS()

  const PLATFORMS = [
    {
      icon: <Icon id="facebook" width="40" height="40" />,
      key: 'facebook',
      label: 'Facebook',
      enabled: !!enableFacebook.value,
    },
    {
      icon: <Icon id="telegram" width="40" height="40" />,
      key: 'telegram',
      label: 'Telegram',
      enabled: !!enableTelegram.value,
    },
    {
      icon: <Icon id="google" width="40" height="40" />,
      key: 'google',
      label: 'Google',
      enabled: !!enableGoogle.value,
    },
    {
      icon: <Icon id="twitter-2" width="40" height="40" />,
      key: 'twitter',
      label: 'Twitter',
      enabled: !!enableTwitter.value,
    },
    {
      icon: <Icon id="twitch" width="40" height="40" />,
      key: 'twitch',
      label: 'Twitch',
      enabled: !!enableTwitch.value,
    },
    {
      icon: <Icon id="tiktok" width="40" height="40" />,
      key: 'tiktok',
      label: 'TikTok',
      enabled: !!enableTiktok.value,
    },
    // { icon: <Icon id="whatsapp" width="40" height="40" />, key: 'whatsapp' },
  ] as const

  const FILTERED_PLATFORMS = PLATFORMS.filter(platform => platform.enabled)

  const toastMessage = useModal(ToastMessage)
  const setUserStore = useBoundStore(state => state.user.setUser)
  const [thirdPartyLoading, setThirdPartyLoading] =
    useState<ThirdPartyPlatforms | null>(null)
  const [authUrls, setAuthUrls] = useState<AuthUrls>({})
  const [failedPlatforms, setFailedPlatforms] = useState<
    Set<ThirdPartyPlatforms>
  >(new Set())

  // Cancel any in-flight OAuth poll on unmount so it stops hitting the
  // backend and doesn't call setState after the component is gone.
  const pollCancelledRef = useRef(false)
  // The pending poll's "run the next iteration now" fn + its timer, so a foreground event can
  // fire the next poll immediately instead of waiting out a background-throttled timer.
  const pollResumeRef = useRef<(() => void) | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollIterationRef = useRef(0)
  const pollCapRef = useRef(OAUTH_POLL_MAX_ITERATIONS)
  const stopPopupWatchRef = useRef<(() => void) | null>(null)
  useEffect(
    () => () => {
      pollCancelledRef.current = true
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current)
      stopPopupWatchRef.current?.()
    },
    [],
  )

  // Mobile WebViews (and browser tabs) throttle or pause background timers, so after the user
  // finishes OAuth in the system browser the next poll tick can be up to a minute away. Running
  // the pending poll on visibilitychange makes login complete the moment they return to the app.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') pollResumeRef.current?.()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  // Schedule the next poll iteration and expose it (pollResumeRef) so the visibilitychange effect
  // can run it early. `advanced` guards against the timer and the foreground event both firing.
  const scheduleNextPoll = (runNext: () => void) => {
    let advanced = false
    const advance = () => {
      if (advanced) return
      advanced = true
      pollResumeRef.current = null
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current)
        pollTimerRef.current = null
      }
      if (pollCancelledRef.current) return
      runNext()
    }
    pollResumeRef.current = advance
    pollTimerRef.current = setTimeout(advance, OAUTH_POLL_INTERVAL_MS)
  }

  // Completes a successful OAuth response (from callback.php poll OR native_login.php): exchanges
  // for privateAuth, persists the user, and fires the callback (status 2 = fresh registration).
  const finishOAuthLogin = async (
    info: OAuthLoginInfo,
    user?: { username: string; promo_code: string },
  ) => {
    stopPopupWatchRef.current?.()
    stopPopupWatchRef.current = null
    const password = info.password as string
    const publicAuth = await getPublicAuth()
    const privateAuth = await api.post<
      ApiResponse<string | { auth: string; expired_time: number }>
    >('/link.php', {
      username: info.username,
      password,
      auth: publicAuth,
    })
    if (
      privateAuth.data.status === 1 &&
      typeof privateAuth.data.info !== 'string'
    ) {
      localStorage.setItem('privateAuth', privateAuth.data.info.auth)
    } else {
      setThirdPartyLoading(null)
      toastMessage.show({
        text:
          typeof privateAuth.data.info === 'string'
            ? privateAuth.data.info
            : 'No se pudo completar el inicio de sesión. Inténtalo de nuevo.',
        isValid: false,
      })
      return
    }

    encryptUser({ username: info.username, password })
    // Backend marks a freshly created account so the caller can fire registration (not login)
    // analytics.
    const isNewRegister = info.is_new_register === 1
    delete info.password
    delete info.is_new_register
    setUserStore(info)
    // Register this device's FCM token against the now-authenticated member.
    void syncFcmToken()

    setThirdPartyLoading(null)
    // In the lite-app browser OAuth flow the sign-in opened in the system browser; ask the wrapper
    // to dismiss it and return. No-op on plain web, native Google Sign-In, and older app builds.
    window.Fun88Native?.returnToApp?.()
    callback?.(info, user || isNewRegister ? 2 : 1)
  }

  const checkLoginStatusPoll = async (
    hash: string,
    platform: ThirdPartyPlatforms,
    adjust: { webUuid: string; attribution: Partial<AdjustAttributionMeta> },
    user?: {
      username: string
      promo_code: string
    },
    iteration: number = 0,
  ) => {
    if (pollCancelledRef.current) return
    pollIterationRef.current = iteration
    if (iteration >= pollCapRef.current) {
      stopPopupWatchRef.current?.()
      stopPopupWatchRef.current = null
      setThirdPartyLoading(null)
      toastMessage.show({
        text: 'No se pudo completar el inicio de sesión. Inténtalo de nuevo.',
        isValid: false,
      })
      return
    }
    try {
      const deviceId = await getDeviceId()
      const adsInfo = getAdsInfo()
      const response = await api.post<ApiResponse<OAuthLoginInfo | string>>(
        `/oauth/${platform}/callback.php`,
        {
          auth_id: hash,
          action: page.toLowerCase(),
          submit_type: `${platform}_oauth`,
          ...(user ?? {}),
          meta: {
            ...getDeviceInfo(),
            ...Object.fromEntries(params.entries()),
            ...(adsInfo ? adsInfo : {}),
            landing_url: getFirstLandingUrl(),
            device_id: adjust.webUuid || deviceId,
            web_uuid: adjust.webUuid,
            ...adjust.attribution,
          },
        },
      )
      if (pollCancelledRef.current) return
      const { status, info } = response.data

      if ((status === 1 || status === 2) && typeof info !== 'string') {
        await finishOAuthLogin(info, user)
        return
      }

      // status === 0 with info like "Not authorize. Please try again" is the
      // transient "OAuth popup hasn't completed yet" state — fall through to
      // the setTimeout retry. Real terminal failures land in the catch block;
      // the 120-iteration cap handles users who never finish the popup.
    } catch (error) {
      if (pollCancelledRef.current) return
      // A transient failure (network hop while the user is off in the system
      // browser, momentary 5xx) must not kill the poll silently — keep going
      // within the iteration budget; the cap shows the failure toast.
      console.error(error)
    }

    scheduleNextPoll(() =>
      checkLoginStatusPoll(hash, platform, adjust, user, iteration + 1),
    )
  }

  // Native social Sign-In (lite-app build exposing the bridge): the app returns a provider ID token
  // — Google via Credential Manager, Facebook via Limited Login, Apple via AuthenticationServices —
  // which we POST straight to native_login.php. No browser, so Google skips the Android
  // Play-Services "add account to device" dead-end and Facebook/Apple avoid the embedded-WebView
  // rejection. One request, no polling; the response is the same shape as callback.php, so
  // finishOAuthLogin completes it.
  const oauthErrorToast = (provider: NativeProvider) =>
    toastMessage.show({
      text: `No se pudo completar el inicio de sesión con ${NATIVE_PROVIDER_LABELS[provider]}. Inténtalo de nuevo.`,
      isValid: false,
    })

  // POSTs a provider token to native_login.php and completes the session via link.php
  // (finishOAuthLogin). Shared by the lite-app bridge flow and the web Apple JS SDK flow.
  //
  // Exactly one of the two tokens is sent, under the field name that says which it is, because the
  // backend verifies them differently: `id_token` is a raw JWS checked by signature (Google, Apple,
  // and Facebook on iOS), while `access_token` — the only thing Facebook on Android can return —
  // has to be validated with a Graph `debug_token` call. Never decode and re-send claims.
  const nativeLogin = async (
    provider: NativeProvider,
    tokens: { idToken?: string | null; accessToken?: string | null },
  ) => {
    const deviceId = await getDeviceId()
    const adsInfo = getAdsInfo()
    const [webUuid, attribution] = await Promise.all([
      getAdjustWebUUID(),
      getAdjustAttribution(),
    ])
    const response = await api.post<ApiResponse<OAuthLoginInfo | string>>(
      `/oauth/${provider}/native_login.php`,
      {
        // Exactly one, never both — enforced here rather than trusted from the caller. The two
        // fields select different server-side verification paths (signature check vs Graph
        // debug_token), so sending both would leave the backend to guess which was authoritative.
        // The JWT wins if a bridge ever returns both: it is verified offline, and it is what every
        // platform provides except Facebook on Android.
        ...(tokens.idToken
          ? { id_token: tokens.idToken }
          : tokens.accessToken
            ? { access_token: tokens.accessToken }
            : {}),
        action: page.toLowerCase(),
        submit_type: `${provider}_oauth`,
        meta: {
          ...getDeviceInfo(),
          ...Object.fromEntries(params.entries()),
          ...(adsInfo ? adsInfo : {}),
          landing_url: getFirstLandingUrl(),
          device_id: webUuid || deviceId,
          web_uuid: webUuid,
          ...attribution,
        },
      },
    )
    const { status, info } = response.data
    if ((status === 1 || status === 2) && typeof info !== 'string') {
      await finishOAuthLogin(info)
    } else if (typeof info === 'string') {
      toastMessage.show({ text: info, isValid: false })
    } else {
      oauthErrorToast(provider)
    }
  }

  const nativeOAuthLogin = async (provider: NativeProvider) => {
    const signIn = nativeSignIn(provider)
    if (!signIn) return
    setThirdPartyLoading(provider)
    const showError = () => oauthErrorToast(provider)

    // The full native chain — bridge → native_login.php → link.php (finishOAuthLogin).
    const run = async () => {
      const { idToken, accessToken, error } = await signIn()
      // Whichever the platform could produce: a JWT everywhere except Facebook on Android, which
      // returns an access token. `nativeLogin` names the field so the backend knows which it got.
      if (!idToken && !accessToken) {
        // Only a user-cancelled sheet/chooser is silent; empty or failed results surface an error.
        if (error !== 'cancelled') {
          // Log the tag: the toast is deliberately generic, so without this a bridge failure is
          // indistinguishable from a backend one when debugging on a device.
          console.error(`native-${provider}-signin`, error)
          showError()
        }
        return
      }
      await nativeLogin(provider, { idToken, accessToken })
    }

    try {
      // Bound the WHOLE chain (not just the bridge): if the bridge, native_login.php, or link.php
      // never resolves, the timeout rejects here so the buttons can't stay stuck-disabled.
      await Promise.race([
        run(),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error(`native-${provider}-timeout`)),
            NATIVE_SIGN_IN_TIMEOUT_MS,
          ),
        ),
      ])
    } catch (error) {
      // Timeout, or a native sign-in / network failure: surface it — we bypassed browser OAuth, so
      // silence would look like a dead button.
      console.error(error)
      showError()
    } finally {
      // Always release the buttons — the success path (finishOAuthLogin) and every failure/timeout.
      setThirdPartyLoading(null)
    }
  }

  // Web Apple JS SDK flow (no bridge): the AppleIDSignInOnSuccess listener hands over the raw
  // id_token; complete it through the same native_login.php chain as the bridge flow.
  useAppleLogin(setThirdPartyLoading, async (idToken: string) => {
    try {
      await nativeLogin('apple', { idToken })
    } catch (error) {
      // Network / server failure: never leave the Apple button spinning.
      console.error(error)
      oauthErrorToast('apple')
    } finally {
      setThirdPartyLoading(null)
    }
  })

  // The enabled flags load asynchronously with the settings, so the fetch
  // effect must re-run as platforms appear — with [] deps a first render
  // before settings arrived meant no auth URL was ever fetched and the
  // social buttons could never render. The ref keeps re-runs from
  // re-fetching platforms already requested.
  const requestedPlatformsRef = useRef(new Set<ThirdPartyPlatforms>())
  const enabledPlatformKeys = FILTERED_PLATFORMS.map(p => p.key).join(',')

  useEffect(() => {
    const markFailed = (platform: ThirdPartyPlatforms) =>
      setFailedPlatforms(prev => new Set(prev).add(platform))

    const getAuthUrl = async (platform: ThirdPartyPlatforms) => {
      try {
        // In the lite app, tell the backend where to bounce the OAuth callback so it foregrounds
        // the app (and closes the browser) instead of stranding the user on the blank callback
        // page. The app exposes the full return URL; on web it's absent, so the backend keeps its
        // default behaviour.
        const redirectScheme = window.Fun88Native?.oauthRedirectUrl
        const response = await api.get<
          ApiResponse<
            | {
                url: string
                id: string
              }
            | string
          >
        >(
          `/oauth/${platform}/authenticate.php`,
          redirectScheme ? { redirect_scheme: redirectScheme } : undefined,
        )
        const { info } = response.data

        if (typeof info !== 'string') {
          setAuthUrls(prev => ({ ...prev, [platform]: info }))
        } else {
          markFailed(platform)
        }
      } catch {
        markFailed(platform)
      }
    }

    FILTERED_PLATFORMS.forEach(platform => {
      if (requestedPlatformsRef.current.has(platform.key)) return
      requestedPlatformsRef.current.add(platform.key)
      getAuthUrl(platform.key)
    })
  }, [enabledPlatformKeys])

  if (FILTERED_PLATFORMS.length === 0 && !showApple) return null

  return (
    <div className="mt-2 xs:mt-3 flex flex-wrap items-center justify-center gap-3">
      {FILTERED_PLATFORMS.map(platform => {
        // Its browser-OAuth URL failed to load → show the icon greyed-out and disabled.
        if (failedPlatforms.has(platform.key)) {
          return (
            <button
              className={`${ICON_BTN_CLASS} cursor-not-allowed opacity-40`}
              type="button"
              key={platform.key}
              title={`${platform.label} (no disponible)`}
              disabled>
              {platform.icon}
            </button>
          )
        }
        // Its browser-OAuth URL hasn't loaded yet — render nothing until it does.
        if (!authUrls[platform.key]) return null
        if (thirdPartyLoading === platform.key) {
          return (
            <Spinner
              key={platform.key}
              containerStyle={{ width: 52, height: 52 }}
            />
          )
        }
        return (
          <button
            className={ICON_BTN_CLASS}
            type="button"
            key={platform.key}
            title={platform.label}
            onClick={async () => {
              // Native Sign-In: a lite-app build exposing the bridge for this provider does it
              // entirely natively (SDK → native_login.php), skipping the browser — and with it
              // Google's "add account to device" interstitial and Facebook's browser hop. Any
              // provider without a bridge method falls through to browser OAuth below.
              if (
                (platform.key === 'google' || platform.key === 'facebook') &&
                nativeSignIn(platform.key)
              ) {
                await nativeOAuthLogin(platform.key)
                return
              }
              const oauthUrl = authUrls[platform.key]?.url ?? ''
              pollCapRef.current = OAUTH_POLL_MAX_ITERATIONS
              pollIterationRef.current = 0
              stopPopupWatchRef.current?.()
              stopPopupWatchRef.current = null
              // Only take the main-frame path in a lite-app build that advertises OAuth support
              // (nativeSupportsOAuth) — its wrapper opens external main-frame navigations in the
              // system browser (Google rejects embedded WebViews with "disallowed_useragent") and
              // keeps this page alive so the poll below completes. Everywhere else — plain web AND
              // older lite-app builds that lack the flag — keep window.open, so a tap can never
              // navigate the WebView away from a build that can't complete the OAuth return flow.
              if (nativeSupportsOAuth()) {
                window.location.href = oauthUrl
              } else {
                const popup = window.open(oauthUrl, '_blank')
                if (!popup) {
                  toastMessage.show({
                    text: 'Permite las ventanas emergentes para continuar con el inicio de sesión.',
                    isValid: false,
                  })
                  return
                }
                stopPopupWatchRef.current = watchPopupClosed(popup, () => {
                  pollCapRef.current = Math.min(
                    pollCapRef.current,
                    pollIterationRef.current + POLLS_AFTER_POPUP_CLOSED,
                  )
                })
              }
              setThirdPartyLoading(platform.key)
              const [webUuid, attribution] = await Promise.all([
                getAdjustWebUUID(),
                getAdjustAttribution(),
              ])
              checkLoginStatusPoll(
                authUrls[platform.key]?.id ?? '',
                platform.key,
                { webUuid, attribution },
              )
            }}
            disabled={!!thirdPartyLoading}>
            {platform.icon}
          </button>
        )
      })}
      {showApple &&
        (thirdPartyLoading === 'apple' ? (
          <Spinner containerStyle={{ width: 52, height: 52 }} />
        ) : (
          <button
            className={`${ICON_BTN_CLASS} text-black dark:text-white`}
            type="button"
            title="Apple"
            onClick={async () => {
              // iOS lite-app build exposing the bridge does Apple entirely natively
              // (AuthenticationServices → native_login.php), skipping the browser. Web and Android
              // (no native Apple SDK) fall back to the Apple JS SDK's browser OAuth.
              if (window.Fun88Native?.appleSignIn) {
                await nativeOAuthLogin('apple')
                return
              }
              // Read off `window`: the SDK is loaded `async defer`, so the bare
              // `AppleID` identifier throws ReferenceError when the script has
              // not executed — and thrown from here it stranded the spinner,
              // since setThirdPartyLoading(null) never ran.
              if (!window.AppleID?.auth) {
                oauthErrorToast('apple')
                return
              }
              setThirdPartyLoading('apple')
              window.AppleID.auth.signIn()
            }}
            disabled={!!thirdPartyLoading}>
            <Icon id="apple" />
          </button>
        ))}
    </div>
  )
}

export default SocialMediaAuth
